import { useClickAway } from "ahooks";
import dayjs, { Dayjs } from "dayjs";
import timezonePlugin from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";

import React, { useEffect, useRef, useState } from "react";
import CalendarPopup from "../CalendarPopup/CalendarPopup";
import { DATE_FORMAT } from "../../../constants/dates";
import classNames from "classnames";

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const HAS_OFFSET_OR_Z = /(?:[zZ]|[+-]\d{2}:\d{2})$/;

const parseDateInTimezone = (
  value: string | Dayjs | undefined,
  timezone: string,
) => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value)) {
    const parsedDate = dayjs(value).tz(timezone);
    return parsedDate.isValid() ? parsedDate : null;
  }

  const parsedDate = HAS_OFFSET_OR_Z.test(value)
    ? dayjs.utc(value).tz(timezone)
    : dayjs.tz(value, timezone);

  return parsedDate.isValid() ? parsedDate : null;
};

interface DatePickerProps {
  id?: string;
  label?: string | JSX.Element;
  disabled?: boolean;
  value?: string | Dayjs;
  timezone?: string;
  availableRange?: { start: Dayjs; end: Dayjs };
  defaultVisible?: Dayjs;
  onChange: (date: string) => void;
  error?: string | JSX.Element;
  containerClassName?: string;
  placeholder?: string | JSX.Element;
}

const DatePicker = ({
  id,
  label,
  availableRange,
  defaultVisible,
  value,
  timezone,
  onChange,
  disabled,
  error,
  placeholder = "Select date...",
  containerClassName,
}: DatePickerProps) => {
  const resolvedTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [_value, _setValue] = useState(
    parseDateInTimezone(value, resolvedTimezone),
  );
  const [calendarOpened, setCalendarOpened] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useClickAway(() => {
    setCalendarOpened(false);
  }, [ref, popupRef]);

  useEffect(() => {
    _setValue(parseDateInTimezone(value, resolvedTimezone));
  }, [value, resolvedTimezone]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setCalendarOpened(true);
    }
    if (e.key === 'Escape') {
      setCalendarOpened(false);
    }
  };

  return (
    <div
      ref={ref}
      className={classNames("min-w-0 font-sans", containerClassName)}
    >
      {label && (
        <label
          htmlFor={id}
          className={classNames(
            "mb-1.5 block text-sm font-medium text-primary-700 dark:text-primary-300",
            disabled && "opacity-50"
          )}
        >
          {label}
        </label>
      )}
      <div className="relative flex min-w-0">
        <div
          ref={inputRef}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-haspopup="dialog"
          aria-expanded={calendarOpened}
          aria-label={label?.toString() || "Date picker"}
          onClick={() => !disabled && setCalendarOpened(true)}
          onKeyDown={handleKeyDown}
          className={classNames(
            !disabled && "cursor-pointer",
            "flex h-10 min-w-0 flex-1 items-center rounded-lg border-2 bg-white px-3 pl-10 text-sm transition-colors dark:bg-content-900",
            disabled
              ? "cursor-not-allowed border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-content-900/50"
              : calendarOpened
                ? "border-primary-400 dark:border-primary-400"
                : "border-primary-200 hover:border-primary-300 dark:border-primary-800 dark:hover:border-primary-700",
            error && "border-error-300 dark:border-error-700",
          )}
        >
          {_value ? (
            <span className="min-w-0 truncate text-primary-700 dark:text-content-100">
              {_value.format(DATE_FORMAT)}
            </span>
          ) : (
            <span className="text-primary-400 dark:text-content-500">
              {placeholder}
            </span>
          )}
        </div>

        <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
          <div className={classNames(
            "icon-[ph--calendar] h-4 w-4",
            disabled
              ? "text-primary-400 dark:text-primary-600"
              : "text-primary-500 dark:text-primary-500"
          )} />
        </div>
      </div>

      {error && (
        <p
          className="mt-1.5 text-sm font-medium text-error-600 dark:text-error-400"
          id={`${id}-error`}
        >
          {error}
        </p>
      )}

      <>
        {calendarOpened && (
          <CalendarPopup
            ref={popupRef}
            anchorRef={inputRef}
            mode="date"
            defaultVisible={defaultVisible}
            availableRange={availableRange}
            timezone={resolvedTimezone}
            onSelect={(date: string) => {
              _setValue(parseDateInTimezone(date, resolvedTimezone));
              onChange(date);
              setCalendarOpened(false);
              inputRef.current?.focus();
            }}
          />
        )}
      </>
    </div>
  );
};

export default DatePicker;
